import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

interface Position {
    x: number;
    y: number;
}

interface UseDialogDragOptions {
    id: string | undefined;
    dialogRef: RefObject<HTMLElement | null>;
    enabled?: boolean;
}

const DEFAULT_POSITION: Position = { x: 0, y: 0 };

const storageKey = (id: string): string => `position-${id}`;

function loadPosition(id: string | undefined): Position {
    if (!id) {
        return DEFAULT_POSITION;
    }

    const stored = localStorage.getItem(storageKey(id));
    return stored ? (JSON.parse(stored) as Position) : DEFAULT_POSITION;
}

function clampToViewport(pos: Position, dialog: HTMLElement, currentTranslate: Position): Position {
    const rect = dialog.getBoundingClientRect();

    // Base (un-translated) position
    const baseLeft = rect.left - currentTranslate.x;
    const baseTop = rect.top - currentTranslate.y;
    const width = rect.width;
    const height = rect.height;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Max translate so the dialog stays fully within the viewport
    const minX = -baseLeft;
    const maxX = vw - baseLeft - width;
    const minY = -baseTop;
    const maxY = vh - baseTop - height;

    return {
        x: Math.min(Math.max(pos.x, minX), maxX),
        y: Math.min(Math.max(pos.y, minY), maxY)
    };
}

export function useDialogDrag({ id, dialogRef, enabled = false }: UseDialogDragOptions) {
    const [translate, setTranslate] = useState<Position>(() =>
        enabled ? loadPosition(id) : DEFAULT_POSITION
    );
    const startPos = useRef<Position>(DEFAULT_POSITION);
    const startTranslate = useRef<Position>(DEFAULT_POSITION);
    const translateRef = useRef<Position>(translate);
    translateRef.current = translate;

    useEffect(() => {
        if (!enabled || !id) {
            return;
        }

        if (translate.x === 0 && translate.y === 0) {
            localStorage.removeItem(storageKey(id));
            return;
        }

        localStorage.setItem(storageKey(id), JSON.stringify(translate));
    }, [id, enabled, translate]);

    // Re-clamp on window resize
    useEffect(() => {
        if (!enabled) {
            return;
        }

        const handleResize = () => {
            const dialog = dialogRef.current;
            if (!dialog) {
                return;
            }

            const clamped = clampToViewport(translateRef.current, dialog, translateRef.current);
            if (clamped.x !== translateRef.current.x || clamped.y !== translateRef.current.y) {
                setTranslate(clamped);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [enabled, dialogRef]);

    const onPointerDown = useCallback(
        (e: React.PointerEvent) => {
            if (!enabled) {
                return;
            }

            const target = e.target as HTMLElement;
            if (target.closest('button, input, select, textarea, a')) {
                return;
            }

            e.preventDefault();
            startPos.current = { x: e.clientX, y: e.clientY };
            startTranslate.current = { ...translateRef.current };

            const onPointerMove = (moveEvent: PointerEvent) => {
                const dialog = dialogRef.current;
                if (!dialog) {
                    return;
                }

                const dx = moveEvent.clientX - startPos.current.x;
                const dy = moveEvent.clientY - startPos.current.y;

                const unclamped = {
                    x: startTranslate.current.x + dx,
                    y: startTranslate.current.y + dy
                };

                const clamped = clampToViewport(unclamped, dialog, translateRef.current);
                setTranslate(clamped);
            };

            const onPointerUp = () => {
                document.removeEventListener('pointermove', onPointerMove);
                document.removeEventListener('pointerup', onPointerUp);
            };

            document.addEventListener('pointermove', onPointerMove);
            document.addEventListener('pointerup', onPointerUp);
        },
        [enabled, dialogRef]
    );

    return { translate, onPointerDown: enabled ? onPointerDown : undefined };
}
